import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { StyledBlackInformationTypography, StyledStack } from "../../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import Confirmation from "../../../../components/commonComponents/ConfirmationBox/Confirmation";
import {
    Close,
    KeyboardArrowDown,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    KeyboardArrowUp,
    KeyboardDoubleArrowDown,
    KeyboardDoubleArrowLeft,
    KeyboardDoubleArrowRight,
    KeyboardDoubleArrowUp,
    Save
} from "@mui/icons-material";
import { updateStateData } from "../../../../Store/actions";
import DualListBox from "react-dual-listbox";
import SingleSectionPreview from "../Previews/SingleSectionPreview";
import { fetchReportFilters, formatImpressionCompanyLabel, getTenantCompanyId, resetNewSectionDetails, saveNewSectionForWorkspace } from "../../../../api_calls/WorkspaceSections";


const NewWorkspaceSection = ({ showModal, closeModal, sectionDetails, sectionType, usedFor, sectionCompany }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const [fieldError, setFieldError] = React.useState({});
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [showPreview, setShowPreview] = React.useState(false);


    const handleFieldChange = (sectionData) => {
        const selectedData =
            typeof currentStateData.workspace_section_selections !== "undefined" ? currentStateData.workspace_section_selections : {};
        dispatchAction(
            updateStateData({
                workspace_section_selections: { ...selectedData, ...sectionData },
            })
        );
    };

    const setSelectedOptions = (selectedOptions) => {
        dispatchAction(
            updateStateData({
                workspace_filter_selections: [...selectedOptions],
            })
        );
    };

    const saveNewSection = async () => {
        setFieldError({});
        saveNewSectionForWorkspace(sectionType, usedFor).then((res) => {
            if (res.error === true) {
                setFieldError({ error: true, ...res.error_messages });
            } else if (res.success === true) {
                resetNewSectionDetails();
                closeModal(false);
            }
        });
    };

    React.useEffect(() => {
        fetchReportFilters().then(() => {
            if (typeof sectionDetails !== "undefined" && Object.keys(sectionDetails).length > 0) {
                dispatchAction(
                    updateStateData({
                        workspace_section_selections: {
                            section_name: sectionDetails.name,
                            embeddable_section_id: sectionDetails.id_embeddable,
                            section_description: sectionDetails.description,
                            section_id: sectionDetails.id
                        },
                    })
                );
                dispatchAction(
                    updateStateData({
                        workspace_filter_selections: (sectionDetails.filters ?? []).map(filter => filter.id),
                    })
                );
            } else if (typeof sectionCompany?.id !== "undefined" && sectionCompany.id !== null) {
                // Missing impression rows create a section for a specific retailer company.
                dispatchAction(
                    updateStateData({
                        workspace_section_selections: {
                            company_id: sectionCompany.id,
                            company_name: sectionCompany.name,
                        },
                    })
                );
            }
        })
    }, [dispatchAction, sectionCompany?.id, sectionCompany?.name, sectionDetails]);

    return (
        <>
            <Dialog
                open={showModal}
                onClose={(e) => {
                    e.preventDefault();
                }}
                fullWidth={true}
                maxWidth={"md"}
            >
                <DialogTitle>
                    {
                        usedFor === "add" ? "Add Report Section" : "Edit Report Section"
                    }
                </DialogTitle>
                <DialogContent>
                    {/* Impressions add/edit: company context + impressions-specific intro copy.
                        Add mode is opened from the missing-row Edit icon (error tooltip) and
                        receives company via sectionCompany; edit uses the section row itself. */}
                    {sectionType === "impression" ? (
                        <>
                            <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0 }}>
                                Company:{" "}
                                {formatImpressionCompanyLabel(
                                    sectionCompany?.name
                                        ?? sectionDetails?.company_name
                                        ?? sectionDetails?.__retailerCompanyName,
                                    sectionCompany?.id
                                        ?? sectionDetails?.__retailerCompanyId,
                                    getTenantCompanyId()
                                )}
                            </StyledBlackInformationTypography>
                            <Box height={10} />
                            <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                                Specify the details for the impressions section.
                                The configured details are used for all impressions sections of campaigns created by users of this company.
                                Changes take effect immediately after saving and also affect already published impressions reports.
                                A preview of the impressions section is available at the bottom of this window.
                            </StyledBlackInformationTypography>
                            <Box height={10} />
                            <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                                <strong>Important:</strong> Ensure that the background filters used by Brand Sharing are always available in the embeddable dashboards.
                            </StyledBlackInformationTypography>
                        </>
                    ) : (
                        <>
                            <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                                Please specify the details for the new report section. The Report section will be available for all future campaigns of this workspace. Already published reports are not affected by new sections. You may preview the new section at the bottom of this window.
                            </StyledBlackInformationTypography>
                            <Box height={10} />
                            <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                                Please always ensure that the background filters used by Brand Sharing are available at all times in the embeddable dashboards.
                            </StyledBlackInformationTypography>
                        </>
                    )}
                    <Box height={20} />
                    {/* ------- Section name ------- */}
                    <StyledStack direction={"row"} spacing={2}>
                        <TextInputField
                            fieldRequired={true}
                            elemId="section_name"
                            label="Section Name (Visible in CAP Explorer)"
                            fieldName="section_name"
                            showlabel={true}
                            defaultVal={
                                typeof currentStateData.workspace_section_selections !== "undefined" &&
                                    typeof currentStateData.workspace_section_selections.section_name !== "undefined"
                                    ? currentStateData.workspace_section_selections.section_name
                                    : ""
                            }
                            inputType={"text"}
                            onChangeCall={(e) => {
                                handleFieldChange({
                                    section_name: e.target.value,
                                });
                            }}
                            fieldError={
                                typeof fieldError.error !== "undefined" &&
                                    fieldError.error === true &&
                                    typeof fieldError.section_name !== "undefined"
                                    ? fieldError.section_name
                                    : ""
                            }
                            errorStyle="tooltip"
                        />
                        <TextInputField
                            fieldRequired={true}
                            elemId="embeddable_section_id"
                            label="Embeddable ID (dashboard ID in Embeddable)"
                            fieldName="embeddable_section_id"
                            showlabel={true}
                            defaultVal={
                                typeof currentStateData.workspace_section_selections !== "undefined" &&
                                    typeof currentStateData.workspace_section_selections.embeddable_section_id !== "undefined"
                                    ? currentStateData.workspace_section_selections.embeddable_section_id
                                    : ""
                            }
                            inputType={"text"}
                            onChangeCall={(e) => {
                                handleFieldChange({
                                    embeddable_section_id: e.target.value,
                                });
                            }}
                            fieldError={
                                typeof fieldError.error !== "undefined" &&
                                    fieldError.error === true &&
                                    typeof fieldError.embeddable_section_id !== "undefined"
                                    ? fieldError.embeddable_section_id
                                    : ""
                            }
                            errorStyle="tooltip"
                        />
                    </StyledStack>

                    <Box height={10} />
                    {/* ------- section description ------- */}
                    <StyledStack direction={"row"}>
                        <TextInputField
                            fieldRequired={false}
                            elemId="section_desc"
                            label="Section Description"
                            fieldName="section_description"
                            showlabel={true}
                            defaultVal={
                                typeof currentStateData.workspace_section_selections !== "undefined" &&
                                    typeof currentStateData.workspace_section_selections.section_description !== "undefined"
                                    ? currentStateData.workspace_section_selections.section_description
                                    : ""
                            }
                            inputType={"text"}
                            onChangeCall={(e) => {
                                handleFieldChange({
                                    section_description: e.target.value,
                                });
                            }}
                            fieldError={
                                typeof fieldError.error !== "undefined" &&
                                    fieldError.error === true &&
                                    typeof fieldError.section_description !== "undefined"
                                    ? fieldError.section_description
                                    : ""
                            }
                            errorStyle="tooltip"
                        />
                    </StyledStack>
                    <Box height={10} />
                    <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0 }}>
                        Filter Selection
                    </StyledBlackInformationTypography>
                    <Box height={10} />
                    <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                        Please specify the filters which you want to show for this section. You must ensure that the selected filters are also supported by the embeddable dashboard specified above. If a filter is missing from the list, it is currently not supported by CAP Explorer and would need to be developed. The name of the filter is also the keyword which needs to be used with embeddable dashboards so that the filter works with CAP Explorer.
                    </StyledBlackInformationTypography>
                    <Box height={12} />
                    <DualListBox
                        id="workspaceSectionsFilterListBox"
                        preserveSelectOrder={true}
                        showOrderButtons={true}
                        options={
                            typeof currentStateData.workspace_report_filters !== "undefined"
                                ? Object.values(
                                    currentStateData.workspace_report_filters.reduce((groups, filter) => {
                                        if (!groups[filter.type]) {
                                            groups[filter.type] = {
                                                label: filter.type.charAt(0).toUpperCase() + filter.type.slice(1),   // group label
                                                options: [],
                                            };
                                        }
                                        groups[filter.type].options.push({
                                            value: filter.id,
                                            label: `${filter.keyword}`,
                                        });

                                        return groups;
                                    }, {})
                                )
                                : []
                        }
                        selected={
                            typeof currentStateData.workspace_filter_selections !== "undefined"
                                ? currentStateData.workspace_filter_selections
                                : []
                        }
                        onChange={(newValue) => {
                            setSelectedOptions(newValue);
                        }}
                        icons={{
                            moveToAvailable: <KeyboardArrowLeft />,
                            moveAllToAvailable: <KeyboardDoubleArrowLeft />,
                            moveToSelected: <KeyboardArrowRight />,
                            moveAllToSelected: <KeyboardDoubleArrowRight />,
                            moveDown: <KeyboardArrowDown />,
                            moveUp: <KeyboardArrowUp />,
                            moveTop: <KeyboardDoubleArrowUp />,
                            moveBottom: <KeyboardDoubleArrowDown />,
                        }}
                    />

                    <Box height={10} />
                    <ActionButton
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPreview(true);
                        }}
                        label={"Preview Dashboard"}
                        icon={<></>}
                    />
                </DialogContent>
                <Box height={20} />
                <DialogActions>
                    <Stack direction={"row"} spacing={2}>
                        <CloseButton
                            onClick={(e) => {
                                e.preventDefault();
                                resetNewSectionDetails();
                                closeModal(false);
                            }}
                            label="Cancel"
                            icon={<Close />}
                        />
                        <ActionButton
                            onClick={(e) => {
                                e.preventDefault();
                                saveNewSection();
                            }}
                            label={"Save"}
                            icon={<Save />}
                        />
                    </Stack>
                </DialogActions>
            </Dialog>
            {/* ------- confirmation popup (on click of save button) -------- */}
            {showConfirmation && (
                <Confirmation
                    showConfirmationPopup={showConfirmation}
                    closePopup={(e) => {
                        setShowConfirmation(false);
                    }}
                    confirmationActionCall={() => {
                        setShowConfirmation(false);
                    }}
                    confirmationMessage={
                        typeof currentStateData.report_settings !== "undefined" &&
                            typeof currentStateData.report_settings.campaign_settings_saved !== "undefined" &&
                            currentStateData.report_settings.campaign_settings_saved === true ? (
                            <>
                                Saving will update your campaign's section details to the current workspace details. The campaign's
                                visibility settings are only adjusted if you save the Report Settings. <Box height={10} /> Are you sure you want to
                                proceed?
                            </>
                        ) : (
                            "Are you sure you want to confirm and apply all the changes you have made?"
                        )
                    }
                />
            )}
            {/* -------- dashboard preview ----------- */}
            {
                showPreview === true && (
                    <SingleSectionPreview
                        showModal={showPreview}
                        closeModal={() => {
                            setShowPreview(false);
                        }}
                        secType={sectionType}
                    />
                )
            }
        </>
    );
};

export default NewWorkspaceSection;
